/* eslint-disable react/no-unstable-nested-components */
import { useSpecificSetting } from "components/settings/hooks/useSpecificSetting";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import { Maximize2Icon, Minimize2Icon } from "lucide-react";
import { parseAsBoolean, useQueryState } from "nuqs";
import { useCallback, type HTMLAttributes } from "react";
import { useIntl } from "react-intl";

interface FullBleedButtonProps extends HTMLAttributes<HTMLButtonElement> {
  persistency?: "queryString" | "userSettings";
}

export default function FullBleedButton({ persistency = "userSettings", className, ...props }: FullBleedButtonProps) {
  const intl = useIntl();
  const { value: fullBleedFromSettings, set: setBleedSetting } = useSpecificSetting("financialsTable.fullBleed", false);
  const [currentBleedFromUrl, setBleedViaUrl] = useQueryState("fullBleed", parseAsBoolean.withDefault(false));
  const currentBleedOption = persistency === "userSettings" ? fullBleedFromSettings || false : currentBleedFromUrl;
  const isFullBleed = currentBleedOption === true;

  const toggleBleed = useCallback(() => {
    if (persistency === "userSettings") {
      setBleedSetting((fullBleed) => !fullBleed);
    }
    if (persistency === "queryString") {
      setBleedViaUrl(!currentBleedFromUrl);
    }
  }, [currentBleedFromUrl, persistency, setBleedSetting, setBleedViaUrl]);

  return (
    <SimpleTextTooltip
      content={
        isFullBleed
          ? intl.formatMessage({ defaultMessage: "Retrair tabela", id: "lg/yke" })
          : intl.formatMessage({ defaultMessage: "Expandir tabela", id: "nFCJPn" })
      }
    >
      <button
        {...props}
        disabled={false}
        type="button"
        onClick={() => toggleBleed()}
        className={classNames(
          "rounded-xl border border-slate-200 bg-slate-50/60 p-2 text-slate-600",
          "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
          "focus-visible:outline-none",
          "focus-visible:ring-1 focus-visible:ring-offset-1",
          "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
          "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
          "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
          {
            "dark:bg-violet-600/20 dark:text-violet-200": isFullBleed,
            "bg-violet-50 text-violet-600": isFullBleed,
          },
          className,
        )}
      >
        {isFullBleed ? <Minimize2Icon strokeWidth={2} /> : <Maximize2Icon strokeWidth={2} />}
      </button>
    </SimpleTextTooltip>
  );
}
